import React, { useEffect, useRef, useState } from "react";
import { Icon, Input } from "semantic-ui-react";
import { groupBy, map, filter, sumBy, forEach } from "lodash";
import classNames from "classnames";
import { toast } from "react-toastify";
import { useCategory, useProduct, useOffer } from "../../hooks";
import { addProductCart } from "../../api/cart";
import {
  ProductCustomizeModal,
  OfferConfirmModal,
} from "../../components/Client";
import "./Categories.scss";

export function Categories() {
  const {
    loading: loadingCategories,
    categories,
    getCategories,
  } = useCategory();
  const { loading: loadingProducts, products, getProducts } = useProduct();
  const { loading: loadingOffers, offers, getOffers } = useOffer();
  const sectionRefs = useRef({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);
  // Cambia en cada apertura: el modal se monta de cero (cantidad 1, sin nota
  // ni opciones) — no depende de que el efecto de reinicio llegue a tiempo.
  const [customizeKey, setCustomizeKey] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOfferConfirm, setShowOfferConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategoryId, setActiveCategoryId] = useState(null);

  useEffect(() => {
    getCategories(true);
    getProducts(true);
    getOffers(true);
  }, []);

  const goToCategory = (idCategory) => {
    setActiveCategoryId(idCategory);
    sectionRefs.current[idCategory]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const openCustomize = (product) => {
    setSelectedProduct(product);
    setCustomizeKey((prev) => prev + 1);
    setShowCustomize(true);
  };

  const closeCustomize = () => setShowCustomize(false);

  const addCart = ({ id, quantity, note, optionChoice, extras, unitPrice }) => {
    addProductCart(id, quantity, note, optionChoice, extras, unitPrice);
    toast.success(`${selectedProduct.title} añadido al carrito`);
    setShowCustomize(false);
  };

  const openOfferConfirm = (offer) => {
    setSelectedOffer(offer);
    setShowOfferConfirm(true);
  };

  const closeOfferConfirm = () => setShowOfferConfirm(false);

  const addOfferCart = ({ offer, quantity, items }) => {
    forEach(items, (item) => {
      addProductCart(item.id, quantity, "", "", [], item.price, offer.id, offer.title);
    });
    toast.success(`${offer.title} añadido al carrito`);
    setShowOfferConfirm(false);
  };

  const loading = loadingCategories || loadingProducts || loadingOffers;
  const productsByCategory = groupBy(products, "category");
  const offersByCategory = groupBy(offers, "category");

  const isSearching = searchTerm.trim().length > 0;
  const searchTermLower = searchTerm.trim().toLowerCase();

  const getFilteredProducts = (categoryId) => {
    const categoryProducts = productsByCategory[categoryId] || [];
    if (!isSearching) return categoryProducts;
    return filter(categoryProducts, (product) =>
      product.title.toLowerCase().includes(searchTermLower)
    );
  };

  const visibleCategories = isSearching
    ? filter(categories, (category) => getFilteredProducts(category.id).length > 0)
    : categories;

  useEffect(() => {
    if (loading) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveCategoryId(Number(entry.target.dataset.categoryId));
          }
        });
      },
      { rootMargin: "-130px 0px -70% 0px", threshold: 0 }
    );

    forEach(sectionRefs.current, (el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, visibleCategories]);

  return (
    <div className="menu-client">
      <div className="menu-client__search">
        <Input
          icon={
            isSearching ? (
              <Icon name="close" link onClick={() => setSearchTerm("")} />
            ) : (
              <Icon name="search" />
            )
          }
          fluid
          placeholder="Buscar en todo el Restaurante"
          value={searchTerm}
          onChange={(_, data) => setSearchTerm(data.value)}
        />
      </div>

      {loading ? (
        <p>Cargando</p>
      ) : (
        <div className="menu-client__layout">
          <div className="menu-client__nav-col">
            <h3 className="menu-client__nav-title">Categorias</h3>

            <nav className="menu-client__nav">
              {map(visibleCategories, (category) => (
                <span
                  key={category.id}
                  className={classNames("menu-client__nav-item", {
                    "menu-client__nav-item--active":
                      activeCategoryId === category.id,
                  })}
                  onClick={() => goToCategory(category.id)}
                >
                  {category.title}
                </span>
              ))}
            </nav>
          </div>

          <div className="menu-client__content">
            {map(visibleCategories, (category) => (
              <div
                key={category.id}
                ref={(el) => (sectionRefs.current[category.id] = el)}
                data-category-id={category.id}
                className="menu-client__section"
              >
                <h4>{category.title}</h4>

                {!isSearching && (offersByCategory[category.id] || []).length > 0 && (
                  <div className="menu-client__offers">
                    {map(offersByCategory[category.id], (offer) => {
                      const originalTotal = sumBy(offer.products_data, (p) =>
                        Number(p.price)
                      );
                      return (
                        <div className="menu-client__offer" key={offer.id}>
                          <div className="menu-client__offer-info">
                            <h5 className="menu-client__offer-title">
                              🎁 {offer.title}
                            </h5>
                            <p className="menu-client__offer-includes">
                              {map(offer.products_data, "title").join(", ")}
                            </p>
                            <p>
                              <span className="menu-client__offer-price-original">
                                S/ {originalTotal.toFixed(2)}
                              </span>
                              <span className="menu-client__offer-price-final">
                                S/ {offer.price}
                              </span>
                            </p>
                          </div>

                          <button
                            type="button"
                            className="menu-client__product-add"
                            onClick={() => openOfferConfirm(offer)}
                          >
                            <Icon name="add" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="menu-client__products">
                  {map(getFilteredProducts(category.id), (product) => (
                    <div
                      key={product.id}
                      className={
                        "menu-client__product" +
                        (product.image
                          ? " menu-client__product--with-image"
                          : "")
                      }
                    >
                      <div className="menu-client__product-info">
                        <h5 className="menu-client__product-title">
                          {product.title}
                        </h5>
                        <p className="menu-client__product-price">
                          S/ {product.price}
                        </p>
                        {product.description && (
                          <p className="menu-client__product-desc">
                            {product.description}
                          </p>
                        )}
                      </div>

                      {product.image && (
                        <div
                          className="menu-client__product-image"
                          style={{ backgroundImage: `url(${product.image})` }}
                        />
                      )}

                      <button
                        type="button"
                        className="menu-client__product-add"
                        onClick={() => openCustomize(product)}
                      >
                        <Icon name="add" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ProductCustomizeModal
        key={customizeKey}
        product={selectedProduct}
        show={showCustomize}
        onClose={closeCustomize}
        onConfirm={addCart}
      />

      <OfferConfirmModal
        offer={selectedOffer}
        show={showOfferConfirm}
        onClose={closeOfferConfirm}
        onConfirm={addOfferCart}
      />
    </div>
  );
}
